import { useTranslation } from 'react-i18next';
import type { Subscription } from '@/api/types';
import { daysLeft as calcDaysLeft } from '@/lib/subscription-progress';
import { shortDate } from '@/lib/date';
import { CardAside, SubCard, type CardTone } from '@/components/ui/tiles';

/**
 * Карта подписки на главной и на вкладке «Подписка». Одна функция решает, что на
 * карте написано, чтобы два экрана не разошлись: статус, тариф, одна строка фактов
 * и число дней (или ∞) в углу. Повторов вида «Навсегда / навсегда» на карте нет:
 * тариф называется один раз, ниже только то, чего в названии нет.
 */
export function SubscriptionCard({ sub, mini, onClick }: { sub: Subscription; mini?: boolean; onClick?: () => void }) {
  const { t, i18n } = useTranslation();
  const frozen = sub.status === 'frozen';
  const lifetime = !!sub.isLifetime;
  const left = sub.daysLeft ?? calcDaysLeft(sub.expiresAt);
  // Последние часы подписки: полных дней 0, но VPN ещё работает.
  const lastHours = left === 0 && new Date(sub.expiresAt).getTime() > Date.now();
  const soon = !lifetime && !frozen && left <= 3;

  const tone: CardTone = frozen ? 'muted' : soon ? 'warn' : 'ok';
  const status = frozen
    ? t('Paused')
    : sub.isTrial
      ? t('Trial period')
      : mini
        ? t('Active')
        : t('Works');

  const devices = t('Up to devices', { count: sub.devices });
  // «Продлится сама» вместо числа устройств: на главной это была отдельная метка, и
  // без неё человек не видел, что деньги спишутся сами.
  const renews = sub.autoRenew && !lifetime && !sub.isTrial && !frozen;
  const meta = lifetime
    ? `${t('No end date')} · ${devices}`
    : `${t('Until date', { date: shortDate(sub.expiresAt, i18n.language) })} · ${renews ? t('Renews itself').toLowerCase() : devices}`;

  return (
    <SubCard
      mini={mini}
      frozen={frozen}
      tone={tone}
      status={status}
      kicker={mini ? undefined : t('Plan kicker')}
      title={sub.planName}
      meta={meta}
      progress={lifetime || !sub.days ? undefined : Math.min(1, left / sub.days)}
      aside={
        lifetime ? (
          <CardAside value="∞" />
        ) : (
          <CardAside value={lastHours ? '<1' : String(left)} unit={t('Days word', { count: lastHours ? 1 : left })} />
        )
      }
      onClick={onClick}
    />
  );
}
